import { AuthUser, AuthSession, UserRole } from '../types';
import { supabase } from './supabaseService';
import { saveMyPhone, stashPendingPhone } from './courierContactService';

// ── Rol-mapping: database (lowercase) ↔ TypeScript enum ──────────────
const DB_ROLE_MAP: Record<string, UserRole> = {
  superuser:  UserRole.SUPERUSER,
  supervisor: UserRole.SUPERVISOR,
  admin:      UserRole.ADMIN,
  pharmacy:   UserRole.PHARMACY,
  courier:    UserRole.COURIER,
  region_manager: UserRole.REGION_MANAGER,
  planner:        UserRole.PLANNER,
};

const ROLE_TO_DB: Record<UserRole, string> = {
  [UserRole.SUPERUSER]:  'superuser',
  [UserRole.SUPERVISOR]: 'supervisor',
  [UserRole.ADMIN]:      'admin',
  [UserRole.PHARMACY]:   'pharmacy',
  [UserRole.COURIER]:    'courier',
  [UserRole.PATIENT]:    'pharmacy', // niet gebruikt voor auth
  [UserRole.REGION_MANAGER]: 'region_manager',
  [UserRole.PLANNER]:        'planner',
};

// ── Demo-accounts (actief als Supabase niet geconfigureerd is) ────────
const SESSION_KEY = 'gobob_session';

export const DEMO_USERS: (AuthUser & { email: string; passwordHash: string })[] = [
  {
    id: 'u1', name: 'Greenspeed HQ', role: UserRole.SUPERUSER,
    email: 'superuser@demo.greenspeed.nl', passwordHash: 'superuser123',
    isPlanner: false,
  },
  {
    id: 'u2', name: 'Beheerder Apotheek de Kroon', role: UserRole.ADMIN,
    pharmacyId: 'ph-1', pharmacyIds: ['ph-1'],
    email: 'admin@demo.greenspeed.nl', passwordHash: 'admin123',
    isPlanner: false,
  },
  {
    id: 'u6', name: 'Regio Beheerder', role: UserRole.ADMIN,
    pharmacyIds: ['ph-1', 'ph-1779784742417'],
    email: 'regio@demo.greenspeed.nl', passwordHash: 'regio123',
    isPlanner: false,
  },
  {
    id: 'u3', name: 'Assistente Apotheek de Kroon', role: UserRole.PHARMACY,
    pharmacyId: 'ph-1',
    email: 'apotheek@demo.greenspeed.nl', passwordHash: 'apotheek123',
    isPlanner: false,
  },
  {
    id: 'u4', name: 'Marco Koerier', role: UserRole.COURIER,
    pharmacyId: 'ph-1', courierId: 'k1',
    email: 'marco@demo.greenspeed.nl', passwordHash: 'koerier123',
    isPlanner: false,
  },
  {
    id: 'u5', name: 'Sanne Bezorgd', role: UserRole.COURIER,
    pharmacyId: 'ph-1', courierId: 'k2',
    email: 'sanne@demo.greenspeed.nl', passwordHash: 'koerier456',
    isPlanner: false,
  },
  {
    id: 'u7', name: 'Lisa Supervisor', role: UserRole.SUPERVISOR,
    email: 'supervisor@demo.greenspeed.nl', passwordHash: 'supervisor123',
    isPlanner: false,
  },
];

// ── Hulpfuncties ──────────────────────────────────────────────────────

function profileToAuthUser(userId: string, profile: Record<string, any>): AuthUser {
  const pharmacyIds: string[] = profile.pharmacy_ids ?? [];
  return {
    id:          userId,
    name:        profile.name,
    role:        DB_ROLE_MAP[profile.role] ?? UserRole.PHARMACY,
    pharmacyIds: pharmacyIds.length > 0 ? pharmacyIds : undefined,
    pharmacyId:  pharmacyIds[0],
    groupId:     profile.group_id ?? undefined,
    // De login-query doet select('*'), dus de standplaats komt al mee. Hij gaat
    // mee in de lokale sessie, zodat een verlaadde pagina hem ook nog heeft.
    home_pharmacy_id: profile.home_pharmacy_id ?? null,
    courierId:   profile.role === 'courier' ? userId : undefined,
    // De login-query doet select('*'), dus is_planner komt al mee; hier alleen
    // de naamgeving. `=== true` omdat de kolom op een database zonder migratie
    // 016 ontbreekt en dan undefined is.
    isPlanner:   profile.is_planner === true,
  };
}

function saveLocalSession(user: AuthUser): void {
  const session: AuthSession = { user, loggedInAt: new Date().toISOString() };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

// ── Login ─────────────────────────────────────────────────────────────

/** Inlogfout die de UI letterlijk aan de gebruiker mag tonen. */
export class LoginError extends Error {
  constructor(
    message: string,
    public code: 'invalid-credentials' | 'email-unconfirmed' | 'rate-limited' | 'profile-missing' | 'auth-failed',
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

/** Vertaalt een Supabase auth-fout naar een begrijpelijke Nederlandse melding. */
function toLoginError(error: { message: string; status?: number }): LoginError {
  const msg = error.message.toLowerCase();
  if (msg.includes('invalid login credentials')) {
    return new LoginError('E-mailadres of wachtwoord onjuist.', 'invalid-credentials');
  }
  if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
    return new LoginError(
      'Je account is nog niet bevestigd. Klik eerst op de link in de bevestigingsmail.',
      'email-unconfirmed',
    );
  }
  if (error.status === 429 || msg.includes('too many requests') || msg.includes('rate limit')) {
    return new LoginError('Te veel inlogpogingen. Probeer het over een paar minuten opnieuw.', 'rate-limited');
  }
  return new LoginError(`Inloggen mislukt: ${error.message}`, 'auth-failed');
}

/**
 * Logt in — bij voorkeur via Supabase Auth, met de vaste demo-accounts als
 * apart spoor.
 *
 * Belangrijk: een mislukte Supabase-login valt hier NIET stilletjes terug op
 * een lokale sessie. Dat maskeerde het echte probleem en leverde een gebruiker
 * op zonder auth.uid(), waardoor elke RLS-query (rooster, koppelcode) leeg
 * terugkwam zonder foutmelding. Echte fouten gaan nu als LoginError naar de UI.
 *
 * @throws {LoginError} bij een mislukte Supabase-login of ontbrekend profiel.
 */
export async function login(email: string, password: string): Promise<AuthUser | null> {
  // Demo-accounts eerst: vaste, niet-bestaande adressen (@demo.greenspeed.nl).
  // Zo blijven ze werken zonder dat een demo-poging als "echte" fout eindigt.
  const demo = DEMO_USERS.find(u =>
    (u.email === email.toLowerCase() || u.name.toLowerCase() === email.toLowerCase()) &&
    u.passwordHash === password
  );
  if (demo) {
    const { passwordHash: _, email: __, ...user } = demo;
    saveLocalSession(user);
    return user;
  }

  // Zonder Supabase blijft alleen het demo-spoor over
  if (!supabase) return null;

  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw toLoginError(error);
  if (!data.user) throw new LoginError('Inloggen mislukt: geen gebruiker ontvangen.', 'auth-failed');

  const { data: profile, error: profileError } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', data.user.id)
    .single();

  if (profileError || !profile) {
    // Een sessie zonder profiel is onbruikbaar (geen rol, geen apotheken);
    // opruimen zodat er geen half-ingelogde staat achterblijft.
    await supabase.auth.signOut().catch(() => {});
    throw new LoginError(
      'Je account heeft nog geen profiel in de database. Vraag de beheerder om je account te koppelen.',
      'profile-missing',
    );
  }

  const user = profileToAuthUser(data.user.id, profile);
  saveLocalSession(user);
  return user;
}

// ── Uitloggen ─────────────────────────────────────────────────────────

export async function logout(): Promise<void> {
  if (supabase) {
    await supabase.auth.signOut().catch(() => {});
  }
  localStorage.removeItem(SESSION_KEY);
}

// ── Sessie ophalen ────────────────────────────────────────────────────

export function getSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

// ── Koerier registreren ───────────────────────────────────────────────

export async function registerCourier(
  name: string,
  email: string,
  password: string,
  phoneE164: string,
): Promise<AuthUser | null> {
  if (!supabase) return null;

  // Geef naam en rol mee als metadata — trigger in de DB maakt user_profiles record aan
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name, role: 'courier' } },
  });
  if (error || !data.user) return null;

  // Het nummer gaat via de courier-contact-functie naar courier_contacts en niet
  // mee in de metadata: handle_new_user() woont in de planner-repo en wordt daar
  // smal gehouden. Is er nog geen sessie (account moet per mail bevestigd worden),
  // dan kan de functie het nummer aan niemand hangen; het wacht dan lokaal tot het
  // slot na de eerste login het oppakt.
  if (data.session) {
    const saved = await saveMyPhone(phoneE164);
    if (!saved.ok) {
      console.warn('[registerCourier] telefoonnummer opslaan mislukt:', saved.reason);
      stashPendingPhone(phoneE164);
    }
  } else {
    stashPendingPhone(phoneE164);
  }

  const user: AuthUser = {
    id:        data.user.id,
    name,
    role:      UserRole.COURIER,
    courierId: data.user.id,
    isPlanner: false,
  };
  saveLocalSession(user);
  return user;
}

// ── Uitnodiging accepteren ────────────────────────────────────────────

export async function acceptInvitation(
  token: string,
  name: string,
  password: string
): Promise<AuthUser | null> {
  if (!supabase) return null;

  // Haal uitnodiging op
  const { data: inviteRows, error: inviteError } = await supabase
    .rpc('get_invitation', { p_token: token });
  const invite = Array.isArray(inviteRows) ? inviteRows[0] : inviteRows;

  if (inviteError || !invite) return null;

  const pharmacyIds = invite.pharmacy_id ? [invite.pharmacy_id] : [];

  // Geef naam, rol en apotheken mee als metadata — trigger maakt user_profiles aan
  const { data, error } = await supabase.auth.signUp({
    email:    invite.email,
    password,
    options:  { data: { name, role: invite.role, pharmacy_ids: pharmacyIds } },
  });
  if (error || !data.user) return null;

  // Markeer uitnodiging als geaccepteerd
  await supabase.rpc('accept_invitation', { p_token: token });

  const user: AuthUser = {
    id:          data.user.id,
    name,
    role:        DB_ROLE_MAP[invite.role] ?? UserRole.PHARMACY,
    pharmacyIds: pharmacyIds.length > 0 ? pharmacyIds : undefined,
    pharmacyId:  pharmacyIds[0],
    isPlanner:   false,
  };
  saveLocalSession(user);
  return user;
}

// ── Apotheekcode koppelen (koerier) ───────────────────────────────────

export type LinkCodeFailure = 'no_session' | 'not_found' | 'error';

export type LinkCodeResult =
  | { ok: true;  pharmacyId: string }
  | { ok: false; reason: LinkCodeFailure };

/** Foutmelding bij een mislukte koppeling — gedeeld door alle koppelschermen. */
export function linkCodeErrorMessage(reason: LinkCodeFailure): string {
  switch (reason) {
    case 'not_found':
      return 'Deze koppelcode bestaat niet. Vraag de apotheek om de actuele code.';
    case 'no_session':
      return 'Je bent niet (meer) ingelogd. Log opnieuw in en probeer het nogmaals.';
    default:
      return 'Koppelen mislukt door een verbindingsfout. Probeer het opnieuw.';
  }
}

/**
 * Koppelt de ingelogde koerier aan de apotheek met deze koppelcode. De code is de
 * permanente pharmacies."courierCode" — hij verloopt niet; de apotheek maakt hem
 * ongeldig door een nieuwe te genereren. De match én de koppeling in
 * courier_pharmacy_access gebeuren server-side in de RPC link_courier_via_code,
 * zodat de koerier de pharmacies-tabel niet hoeft te kunnen lezen.
 */
export async function linkPharmacyCode(code: string): Promise<LinkCodeResult> {
  if (!supabase) return { ok: false, reason: 'error' };

  const normalized = code.trim().toUpperCase();
  if (!normalized) return { ok: false, reason: 'not_found' };

  // De koppeling hangt aan auth.uid(): zonder echte Supabase-sessie kan de RPC
  // niets vastleggen, dus melden we dat expliciet i.p.v. stil te falen.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, reason: 'no_session' };

  const { data, error } = await supabase
    .rpc('link_courier_via_code', { p_code: normalized });

  if (error) {
    console.error('[linkPharmacyCode] RPC mislukt:', error);
    return { ok: false, reason: 'error' };
  }

  const result = (Array.isArray(data) ? data[0] : data) as
    { status?: string; pharmacy_id?: string } | null;

  if (result?.status === 'no_session')                   return { ok: false, reason: 'no_session' };
  if (result?.status !== 'ok' || !result.pharmacy_id)    return { ok: false, reason: 'not_found' };

  const pharmacyId = result.pharmacy_id;

  // Profiel bijwerken zodat de koppeling ook buiten courier_pharmacy_access zichtbaar is
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('pharmacy_ids')
      .eq('id', session.user.id)
      .maybeSingle();

    const existing: string[] = profile?.pharmacy_ids ?? [];
    if (!existing.includes(pharmacyId)) {
      await supabase
        .from('user_profiles')
        .update({ pharmacy_ids: [...existing, pharmacyId] })
        .eq('id', session.user.id);
    }
  } catch (err) {
    console.warn('[linkPharmacyCode] profiel bijwerken mislukt:', err);
  }

  // Lokale sessie bijwerken zodat de koerier de apotheek meteen ziet
  const localSession = getSession();
  if (localSession) {
    const updatedPharmacyIds = Array.from(
      new Set([...(localSession.user.pharmacyIds ?? []), pharmacyId])
    );
    const updatedUser: AuthUser = {
      ...localSession.user,
      pharmacyIds: updatedPharmacyIds,
      pharmacyId:  localSession.user.pharmacyId ?? updatedPharmacyIds[0],
    };
    saveLocalSession(updatedUser);
  }

  return { ok: true, pharmacyId };
}

// ── Permanente koppelcode op de apotheek zetten (apotheker/admin/...) ──

function randomCourierCode(): string {
  // 2 letters + koppelteken + 4 cijfers, bijv. "KR-4821"
  const letters = String.fromCharCode(65 + Math.floor(Math.random() * 26)) +
                  String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const digits  = String(Math.floor(1000 + Math.random() * 9000));
  return `${letters}-${digits}`;
}

/**
 * Genereert (of vervangt) de permanente koppelcode van een apotheek en
 * slaat hem op de pharmacies-tabel op. Retourneert de nieuwe code, of null
 * als er geen cloud is of het opslaan mislukt. Probeert enkele keren i.v.m.
 * de unieke index op courierCode.
 */
export async function setPharmacyCourierCode(pharmacyId: string): Promise<string | null> {
  if (!supabase) return null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = randomCourierCode();
    const { error } = await supabase
      .from('pharmacies')
      .update({ courierCode: code })
      .eq('id', pharmacyId);
    if (!error) return code;
  }
  return null;
}

// ── Gebruiker uitnodigen (apotheker/supervisor) ───────────────────────

export async function inviteUser(
  email: string,
  role: string,
  pharmacyId: string
): Promise<void> {
  if (!supabase) return;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  // Sla uitnodiging op in database
  const { data: invite } = await supabase
    .from('invitations')
    .insert({
      email,
      role,
      pharmacy_id: pharmacyId,
      invited_by:  session.user.id,
    })
    .select('token')
    .single();

  if (!invite) return;

  // Stuur uitnodigingsmail via Netlify function
  await fetch('/.netlify/functions/send-invitation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      role,
      pharmacyId,
      token: invite.token,
    }),
  }).catch(() => {}); // fout in mail-verzending blokkeert niet
}

// ── Apotheken van ingelogde koerier ophalen ───────────────────────────

export async function getCourierPharmacies(): Promise<string[]> {
  if (!supabase) return [];

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return [];

  const { data } = await supabase
    .from('courier_pharmacy_access')
    .select('pharmacy_id')
    .eq('courier_id', session.user.id);

  return data?.map((r: any) => r.pharmacy_id) ?? [];
}

// ── Legacy: saveSession (backwards compat voor App.tsx) ───────────────
export { saveLocalSession as saveSession };
